import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/landing/LanguageContext";
import { trackLead } from "@/landing/pixel";

const Nav = () => {
  const { t, lang, toggle, wa } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-carbon/85 backdrop-blur-xl border-b border-cream/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <a
          href="#top"
          data-testid="nav-logo"
          className="font-serif text-base tracking-tight text-cream sm:text-xl"
        >
          Formación <span className="italic text-sand">Activación Kundalini</span>
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {t.nav.links.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              data-testid={`nav-link-${l.id}`}
              className="font-sans text-xs tracking-[0.18em] uppercase text-cream/70 transition-colors hover:text-sand"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="font-sans text-xs tracking-[0.2em] text-cream/80 transition-colors hover:text-sand"
            aria-label="Toggle language"
          >
            {lang === "es" ? "ES" : "EN"} <span className="text-cream/30">/</span>{" "}
            <span className="text-cream/40">{lang === "es" ? "EN" : "ES"}</span>
          </button>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta"
            onClick={() => trackLead("nav-cta")}
            className="hidden rounded-full bg-sand px-5 py-2.5 font-sans text-xs tracking-[0.18em] uppercase text-carbon transition-colors hover:bg-cream md:inline-block"
          >
            {t.nav.cta}
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            className="text-cream lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden bg-carbon/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {t.nav.links.map((l) => (
                <a
                  key={l.id}
                  href={`#${l.id}`}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${l.id}`}
                  className="border-b border-cream/10 py-3 font-sans text-sm tracking-[0.12em] uppercase text-cream/80"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackLead("mobile-nav-cta"); setOpen(false); }}
                data-testid="mobile-nav-cta"
                className="mt-3 rounded-full bg-sand px-5 py-3 text-center font-sans text-xs tracking-[0.18em] uppercase text-carbon"
              >
                {t.nav.cta}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
